/**
 * DeviceCard —— 控制台核心单元。
 * 按 device.spec.capabilities 渲染三类控件：SwitchControl / AcControl / SensorPanel。
 */
import type { CSSProperties, ReactNode } from 'react';
import type { Capability, Device } from '@znjj/shared';
import { Icon } from './Icon';
import { fmt, READ_LABEL } from './ui';

function readableCaps(device: Device): Capability[] {
  return device.spec.capabilities.filter(
    (c) => c.access === 'r' && device.state[c.code] !== undefined && device.state[c.code] !== null,
  );
}

function Metric({ cap, value }: { cap: Pick<Capability, 'code' | 'type' | 'unit'>; value: unknown }) {
  const dec = cap.type === 'float' ? 1 : 0;
  return (
    <div className="metric">
      <span className="metric__num">
        {fmt.num(value, dec)}
        {cap.unit ? <span className="metric__unit">{cap.unit}</span> : null}
      </span>
      <span className="metric__cap">{READ_LABEL[cap.code] ?? cap.code}</span>
    </div>
  );
}

function StatusTag({ device }: { device: Device }) {
  if (!device.online) {
    return (
      <span className="tag tag--off">
        <i className="dot dot--off" />
        离线
      </span>
    );
  }
  if (!device.claimedAt) {
    return (
      <span className="tag tag--warn">
        <i className="dot dot--warn" />
        未认领
      </span>
    );
  }
  return (
    <span className="tag tag--on">
      <i className="dot dot--on" />
      在线
    </span>
  );
}

/** 开关控件 */
function SwitchControl({
  device,
  onCommand,
}: {
  device: Device;
  onCommand: (code: string, value: unknown) => void;
}) {
  const on = device.state.switch === true;
  return (
    <button
      className="switch"
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="开关"
      disabled={!device.online}
      onClick={() => onCommand('switch', !on)}
    />
  );
}

/** 空调控件：温度步进 + 模式分段 */
function AcControl({
  device,
  onCommand,
}: {
  device: Device;
  onCommand: (code: string, value: unknown) => void;
}) {
  const caps = device.spec.capabilities;
  const tempCap = caps.find((c) => c.code === 'temp_set');
  const modeCap = caps.find((c) => c.code === 'mode');
  const min = tempCap?.min ?? 16;
  const max = tempCap?.max ?? 30;
  const t = Number(device.state.temp_set ?? 26);
  const off = !device.online;

  return (
    <>
      <div className="dev__ctrl">
        <SwitchControl device={device} onCommand={onCommand} />
        <span className="stepper">
          <button
            className="stepper__btn"
            type="button"
            aria-label="调低温度"
            disabled={off || t <= min}
            onClick={() => onCommand('temp_set', t - 1)}
          >
            <Icon name="minus" size={14} />
          </button>
          <span className="stepper__val num">{t}</span>
          <button
            className="stepper__btn"
            type="button"
            aria-label="调高温度"
            disabled={off || t >= max}
            onClick={() => onCommand('temp_set', t + 1)}
          >
            <Icon name="plus" size={14} />
          </button>
          <span className="t-xs dim" style={{ marginLeft: 2 }}>
            °C
          </span>
        </span>
      </div>
      {modeCap ? (
        <div className="row row--wrap" style={{ marginTop: 10 }}>
          <span className="seg">
            {(modeCap.values ?? []).map((v) => (
              <button
                key={v}
                className={'seg__btn' + (device.state.mode === v ? ' is-active' : '')}
                type="button"
                disabled={off}
                onClick={() => onCommand('mode', v)}
              >
                {modeCap.labels?.[v] ?? v}
              </button>
            ))}
          </span>
        </div>
      ) : null}
    </>
  );
}

/** 传感器读数面板（env / th / gas） */
function SensorPanel({ device }: { device: Device }) {
  const caps = readableCaps(device);
  return (
    <div className="grid-metrics">
      {caps.map((c) => (
        <Metric key={c.code} cap={c} value={device.state[c.code]} />
      ))}
    </div>
  );
}

/** 燃气报警器：报警状态 + 阈值步进（与空调温度一致的操作方式）+ 浓度读数 */
function GasPanel({
  device,
  onCommand,
}: {
  device: Device;
  onCommand: (code: string, value: unknown) => void;
}) {
  const gas = Number(device.state.gas ?? 0);
  const alarmCap = device.spec.capabilities.find((c) => c.code === 'gas_alarm');
  const min = alarmCap?.min ?? 1;
  const max = alarmCap?.max ?? 100;
  const step = alarmCap?.step ?? 1;
  const threshold = Number(device.state.gas_alarm ?? 25);
  const alarming = gas >= threshold;
  const gasCap = device.spec.capabilities.find((c) => c.code === 'gas');
  const off = !device.online || !alarmCap;
  return (
    <>
      <div className="dev__ctrl">
        <span className={'tag ' + (alarming ? 'tag--danger' : 'tag--on')}>
          <i className={'dot ' + (alarming ? 'dot--danger' : 'dot--on')} />
          {alarming ? '燃气报警' : '正常'}
        </span>
        <span className="row" style={{ gap: 6 }}>
          <span className="t-xs dim">报警阈值</span>
          <span className="stepper">
            <button
              className="stepper__btn"
              type="button"
              aria-label="调低报警阈值"
              disabled={off || threshold <= min}
              onClick={() => onCommand('gas_alarm', threshold - step)}
            >
              <Icon name="minus" size={14} />
            </button>
            <span className="stepper__val num">{threshold}</span>
            <button
              className="stepper__btn"
              type="button"
              aria-label="调高报警阈值"
              disabled={off || threshold >= max}
              onClick={() => onCommand('gas_alarm', threshold + step)}
            >
              <Icon name="plus" size={14} />
            </button>
            <span className="t-xs dim" style={{ marginLeft: 2 }}>
              %LEL
            </span>
          </span>
        </span>
      </div>
      {gasCap ? (
        <div className="grid-metrics" style={{ marginTop: 10 }}>
          <Metric cap={gasCap} value={device.state.gas} />
        </div>
      ) : null}
    </>
  );
}

function bodyOf(device: Device, onCommand: (code: string, value: unknown) => void) {
  const st = device.state;

  if (device.type === 'light') {
    return (
      <div className="dev__ctrl">
        <SwitchControl device={device} onCommand={onCommand} />
        <span className="num t-sm muted">亮度 {fmt.num(st.bright, 0)}%</span>
      </div>
    );
  }

  if (device.type === 'ac') return <AcControl device={device} onCommand={onCommand} />;

  if (device.type === 'curtain') {
    const pos = Number(st.position ?? 0);
    return (
      <div className="dev__ctrl">
        <SwitchControl device={device} onCommand={onCommand} />
        <span className="row" style={{ gap: 8 }}>
          <span className="seg">
            {[
              { v: 0, label: '全关' },
              { v: 50, label: '半开' },
              { v: 100, label: '全开' },
            ].map((it) => (
              <button
                key={it.v}
                className={'seg__btn' + (pos === it.v ? ' is-active' : '')}
                type="button"
                disabled={!device.online}
                onClick={() => onCommand('position', it.v)}
              >
                {it.label}
              </button>
            ))}
          </span>
        </span>
      </div>
    );
  }

  if (device.type === 'plug') {
    const powerCap = device.spec.capabilities.find((c) => c.code === 'power');
    const on = st.switch === true;
    return (
      <>
        <div className="dev__ctrl">
          <SwitchControl device={device} onCommand={onCommand} />
          <span className="num t-sm muted">{on ? '通电中' : '已断开'}</span>
        </div>
        {powerCap ? (
          <div className="grid-metrics grid-metrics--2" style={{ marginTop: 10 }}>
            <Metric cap={{ code: 'power', type: powerCap.type, unit: powerCap.unit }} value={st.power} />
            <div className="metric">
              <span className="metric__num metric__num--dim">{on ? 'ON' : 'OFF'}</span>
              <span className="metric__cap">开关状态</span>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  if (device.type === 'gas') return <GasPanel device={device} onCommand={onCommand} />;

  return <SensorPanel device={device} />;
}

export function DeviceCard({
  device,
  index,
  onCommand,
  onHistory,
  onToggleClaim,
  onShare,
  onDelete,
}: {
  device: Device;
  index: number;
  onCommand: (code: string, value: unknown) => void;
  onHistory: () => void;
  onToggleClaim: () => void;
  onShare: () => void;
  onDelete: () => void;
}) {
  const on = device.state.switch === true;
  const cls =
    'dev' + (device.online ? '' : ' is-offline') + (on ? ' is-on' : '');

  const meta: ReactNode[] = [];
  if (device.room) meta.push(<span key="room" className="dev__meta-item">{device.room}</span>);
  if (device.rssi !== null) {
    meta.push(
      <span key="rssi" className="dev__meta-item">
        <Icon name="wifi" size={12} />
        {device.rssi}dBm
      </span>,
    );
  }
  if (device.battery !== null) {
    meta.push(
      <span key="bat" className="dev__meta-item">
        <Icon name="battery" size={12} />
        {device.battery}%
      </span>,
    );
  }
  if (device.lastSeenAt) {
    meta.push(
      <span key="seen" className="dev__meta-item">
        <Icon name="clock" size={12} />
        {fmt.hhmm(device.lastSeenAt)}
      </span>,
    );
  }

  return (
    <article className={cls} style={{ '--i': index } as CSSProperties}>
      <div className="dev__top">
        <div className="dev__ident">
          <span className="dev__badge">
            <Icon name={device.spec.icon} size={17} />
          </span>
          <span style={{ minWidth: 0 }}>
            <span className="dev__name">{device.name}</span>
            <span className="dev__meta">{meta}</span>
          </span>
        </div>
        <StatusTag device={device} />
      </div>

      <div className="dev__body">{bodyOf(device, onCommand)}</div>

      <div className="dev__foot">
        <span className="row" style={{ gap: 6 }}>
          <span className="num t-xs dim">{device.id.slice(0, 10)}</span>
          <span className="t-xs dim">{fmt.source(device.source)}</span>
        </span>
        <span className="row" style={{ gap: 12 }}>
          <button className="dev__link" type="button" onClick={onShare}>
            <Icon name="share-2" size={13} />
            分享
          </button>
          <button className="dev__link" type="button" onClick={onHistory}>
            <Icon name="chart-line" size={13} />
            历史
          </button>
          <button className="dev__link" type="button" onClick={onToggleClaim}>
            {device.claimedAt ? '取消认领' : '认领'}
          </button>
          <button className="dev__link dev__link--danger" type="button" onClick={onDelete}>
            <Icon name="trash-2" size={13} />
            删除
          </button>
        </span>
      </div>
    </article>
  );
}
